import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { InstallGuideDialog } from '@/components/pwa/InstallGuideDialog';
import { PWA_APP } from '@/pwa/appTargets';
import { isMobile } from '@/pwa/installContext';
import { claimPrompt, releasePrompt } from '@/pwa/promptCoordinator';

/** Rotas onde o convite nunca aparece (trabalho crítico). */
const SUPPRESSED_PREFIXES = [
  '/login',
  '/esqueci-senha',
  '/redefinir-senha',
  '/auth',
  '/~oauth',
  '/crm/chat',
  '/vendas/nova',
  '/negocios/nova',
  '/instalar',
  '/portal-corretor',
  '/cadastro-corretor',
  '/corretor/primeiro-acesso',
  '/escolher-ambiente',
];

const DELAY_MS = 8000;

/**
 * Convite discreto para instalar o AutoFluxo.
 * Aparece só em produção, fora do editor/preview, e respeita a dispensa.
 */
export function InstallInvite() {
  const { showInstallOption, canPrompt, isIos, dismissed, promptInstall, dismiss } = usePwaInstall();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  const path = location.pathname.toLowerCase();
  const suppressed = SUPPRESSED_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );

  useEffect(() => {
    // No celular, a tela cheia "Instale o AutoFluxo" substitui este banner.
    if (!showInstallOption || dismissed || suppressed || isMobile()) {
      setVisible(false);
      return;
    }
    const timer = window.setTimeout(() => {
      if (claimPrompt('install-banner')) setVisible(true);
    }, DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [showInstallOption, dismissed, suppressed]);

  useEffect(() => {
    if (!visible) releasePrompt('install-banner');
  }, [visible]);

  return (
    <>
      {visible && (
      <div className="fixed inset-x-3 bottom-3 z-[300] mx-auto max-w-md rounded-lg border border-border bg-card p-3 shadow-lg sm:inset-x-auto sm:right-4">
        <div className="flex items-start gap-3">
          <img src={PWA_APP.icon} alt="" aria-hidden className="h-10 w-10 shrink-0 rounded-lg" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">Instalar AutoFluxo</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Adicione o ícone na tela inicial e abra direto, sem passar pelo navegador.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => {
                  if (canPrompt) {
                    void promptInstall();
                  } else if (isIos) {
                    setGuideOpen(true);
                  }
                  setVisible(false);
                }}
              >
                <Download className="mr-2 h-3.5 w-3.5" />
                Instalar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  dismiss();
                  setVisible(false);
                }}
              >
                Agora não
              </Button>
            </div>
          </div>
          <button
            type="button"
            aria-label="Fechar"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setVisible(false)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      )}

      <InstallGuideDialog open={guideOpen} onClose={() => setGuideOpen(false)} />
    </>
  );
}
